import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Dialog, DialogContent } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import AdvancedSearchModal from "../components/AdvancedSearchModal";
import SearchAutocomplete from "../components/SearchAutocomplete";
import {
  EMPTY_SEARCH_FILTER_STATE,
  filterStateToModalInitial,
  filterStateToUrlString,
  modalPayloadToFilterState,
  parseSearchParamsFromUrl,
} from "../utils/searchFilterState";
import { isPopupEquipePath } from "../utils/teamNavigation";

const AdvancedSearchContext = createContext(null);

function isAdvancedSearchShortcut(event) {
  const key = String(event.key || "").toLowerCase();
  if (key !== "f") return false;
  if (!(event.ctrlKey || event.metaKey)) return false;
  if (event.altKey) return false;
  return true;
}

export function AdvancedSearchProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");
  const [initialAdvanced, setInitialAdvanced] = useState(() =>
    filterStateToModalInitial(EMPTY_SEARCH_FILTER_STATE)
  );
  const inCockpit = isPopupEquipePath(location.pathname);

  const openAdvancedSearch = useCallback((options = {}) => {
    setQuickSearchOpen(false);
    setInitialQuery(typeof options.initialQuery === "string" ? options.initialQuery : "");
    setInitialAdvanced(
      options.initialAdvanced != null
        ? options.initialAdvanced
        : filterStateToModalInitial(EMPTY_SEARCH_FILTER_STATE)
    );
    setOpen(true);
  }, []);

  const openQuickSearch = useCallback(() => {
    setOpen(false);
    setQuickSearchOpen(true);
  }, []);

  const openFromShortcut = useCallback(() => {
    // No cockpit (/popup-equipe) não há Topbar: abre a busca rápida primeiro.
    // A busca avançada fica no botão Tune dentro do SearchAutocomplete.
    if (isPopupEquipePath(location.pathname)) {
      openQuickSearch();
      return;
    }

    if (location.pathname.includes("/search-results")) {
      const state = parseSearchParamsFromUrl(location.search);
      openAdvancedSearch({
        initialQuery: state.q?.trim() || "",
        initialAdvanced: filterStateToModalInitial(state),
      });
      return;
    }
    openAdvancedSearch();
  }, [location.pathname, location.search, openAdvancedSearch, openQuickSearch]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isAdvancedSearchShortcut(event)) return;
      event.preventDefault();
      event.stopPropagation();
      if (open || quickSearchOpen) return;
      openFromShortcut();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, openFromShortcut, quickSearchOpen]);

  // Ao sair do cockpit, fecha a busca rápida (rota sem Topbar).
  useEffect(() => {
    if (!inCockpit && quickSearchOpen) {
      setQuickSearchOpen(false);
    }
  }, [inCockpit, quickSearchOpen]);

  const handleApply = useCallback(
    (payload) => {
      const state = modalPayloadToFilterState(payload);
      const path = `/search-results?${filterStateToUrlString(state)}`;
      setOpen(false);

      if (isPopupEquipePath(location.pathname)) {
        const url = `${window.location.origin}${path}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      navigate(path);
    },
    [location.pathname, navigate]
  );

  const value = useMemo(
    () => ({
      openAdvancedSearch,
      openQuickSearch,
      closeAdvancedSearch: () => setOpen(false),
      closeQuickSearch: () => setQuickSearchOpen(false),
      isAdvancedSearchOpen: open,
      isQuickSearchOpen: quickSearchOpen,
    }),
    [open, openAdvancedSearch, openQuickSearch, quickSearchOpen]
  );

  return (
    <AdvancedSearchContext.Provider value={value}>
      {children}
      <AdvancedSearchModal
        open={open}
        onClose={() => setOpen(false)}
        initialQuery={initialQuery}
        initialAdvanced={initialAdvanced}
        onApply={handleApply}
      />
      <Dialog
        open={quickSearchOpen}
        onClose={() => setQuickSearchOpen(false)}
        fullWidth
        maxWidth="sm"
        disableEnforceFocus
        aria-label="Busca"
        PaperProps={{
          sx: {
            mt: { xs: 2, sm: 8 },
            verticalAlign: "top",
            overflow: "visible",
            borderRadius: 2,
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
          },
        }}
      >
        <DialogContent sx={{ p: 2, overflow: "visible" }}>
          {quickSearchOpen ? (
            <SearchAutocomplete
              dialogMode
              autoFocus
              onRequestClose={() => setQuickSearchOpen(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </AdvancedSearchContext.Provider>
  );
}

export function useAdvancedSearch() {
  const ctx = useContext(AdvancedSearchContext);
  if (!ctx) {
    throw new Error("useAdvancedSearch must be used within AdvancedSearchProvider");
  }
  return ctx;
}
